import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { createCoach } from '../utils/coaches';
import { AVAILABLE_AGE_GROUPS } from '../utils/ageGroups';
import SEO from '../components/SEO';
import Header from '../components/Header';
import AuthFooter from '../components/AuthFooter';
import '../styles/AuthPage.css';
import '../styles/CoachSignup.css';

function CoachSignup() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [step, setStep] = useState(1);
  const [teamName, setTeamName] = useState('');
  const [ageGroup, setAgeGroup] = useState('');
  const [markupPerMonth, setMarkupPerMonth] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (step === 1) {
      if (!teamName.trim() || !ageGroup) return;
      setStep(2);
      return;
    }

    setSubmitting(true);
    try {
      const { coachId } = await createCoach(user.uid, {
        name: teamName.trim(),
        teamName: teamName.trim(),
        ageGroup,
        markupPerMonth,
      });
      navigate(`/${user.uid}/settings`, { state: { highlightCoach: coachId } });
    } catch (err) {
      console.error('[CoachSignup] Error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="auth-page">
        <Header />
        <div className="auth-bg-glow auth-bg-glow-1" />
        <div className="auth-bg-glow auth-bg-glow-2" />
        <div className="auth-bg-overlay" />
        <div className="auth-page-center">
          <div className="auth-card" style={{ textAlign: 'center' }}>
            <p style={{ color: 'rgba(255,255,255,0.6)' }}>Loading...</p>
          </div>
        </div>
        <AuthFooter />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="auth-page">
        <SEO title="Coach Signup" description="Set up your team on Begin Strength." path="/coach/signup" />
        <Header />
        <div className="auth-bg-glow auth-bg-glow-1" />
        <div className="auth-bg-glow auth-bg-glow-2" />
        <div className="auth-bg-overlay" />
        <div className="auth-page-center">
          <div className="auth-card" style={{ textAlign: 'center' }}>
            <h2 style={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '36px', margin: 0, lineHeight: 1.1 }}>
              Set Up Your <span style={{ color: '#FF0000' }}>Team</span>
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, margin: 0 }}>
              Create a free account to get started. Already have one? Sign in below.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/signup?redirect=/coach/signup" className="auth-submit" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                Create Account
              </Link>
            </div>
            <p style={{ margin: 0 }}>
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
                Already have an account?{' '}
                <Link to="/signin?redirect=/coach/signup" style={{ color: '#FF0000', textDecoration: 'underline' }}>Sign In</Link>
              </span>
            </p>
          </div>
          <p className="auth-tagline">
            <Link to="/coach" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'underline', fontSize: '14px' }}>Back to Coach Info</Link>
          </p>
        </div>
        <AuthFooter />
      </div>
    );
  }

  return (
    <div className="auth-page">
      <SEO title="Coach Signup" description="Set up your team on Begin Strength." path="/coach/signup" />
      <Header />
      <div className="auth-bg-glow auth-bg-glow-1" />
      <div className="auth-bg-glow auth-bg-glow-2" />
      <div className="auth-bg-overlay" />

      <div className="auth-page-center">
        <div className="auth-card">
          <h2 style={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '36px', margin: 0, textAlign: 'center', lineHeight: 1.1 }}>
            {step === 1 ? (
              <>Set Up Your <span style={{ color: '#FF0000' }}>Team</span></>
            ) : (
              <>Set Your <span style={{ color: '#FF0000' }}>Pricing</span></>
            )}
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, margin: 0, textAlign: 'center' }}>
            {step === 1
              ? "Enter your team or coach page name and select your age group."
              : 'Choose how much to add per athlete on top of the base rate. You keep the difference.'}
          </p>

          <form onSubmit={handleSubmit} className="auth-form">
            {step === 1 && (
              <>
                <div className="auth-input-wrapper">
                  <input
                    id="teamName"
                    type="text"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    placeholder='e.g. "Coach Sam" or "West Side 14s"'
                    required
                    minLength={2}
                    className="auth-input"
                  />
                </div>
                <div className="coach-age-group-select">
                  <label className="coach-field-label">Age Group</label>
                  <div className="club-age-group-grid">
                    {AVAILABLE_AGE_GROUPS.map((ag) => (
                      <button
                        key={ag}
                        type="button"
                        className={`club-age-group-chip ${ageGroup === ag ? 'selected' : ''}`}
                        onClick={() => setAgeGroup(ag)}
                      >
                        {ag}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {step === 2 && (
              <div className="coach-markup-section">
                <label className="coach-field-label">Monthly add-on per athlete</label>
                <p className="coach-markup-hint">
                  Base rate is $3/mo (annual) or $4/mo (monthly). Add up to $5 — you keep the difference. Stripe required when adding &gt; $0 (coming soon).
                </p>
                <div className="coach-markup-options">
                  {[0, 1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={`coach-markup-chip ${markupPerMonth === n ? 'selected' : ''}`}
                      onClick={() => setMarkupPerMonth(n)}
                    >
                      ${n}
                    </button>
                  ))}
                </div>
                <p className="coach-markup-total">
                  Athletes will pay <strong>${markupPerMonth + 3}/mo</strong> (annual) or <strong>${markupPerMonth + 4}/mo</strong> (monthly)
                </p>
              </div>
            )}

            {error && <p className="auth-error">{error}</p>}

            <div style={{ display: 'flex', gap: '12px' }}>
              {step === 2 && (
                <button type="button" className="auth-submit" style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)' }} onClick={() => setStep(1)}>
                  Back
                </button>
              )}
              <button
                type="submit"
                className="auth-submit"
                disabled={submitting || (step === 1 && (!teamName.trim() || !ageGroup))}
                style={{ flex: 1 }}
              >
                {step === 1 ? 'Next: Set Pricing' : submitting ? 'Creating...' : 'Create Team'}
              </button>
            </div>
          </form>
        </div>

        <p className="auth-tagline">
          <Link to="/coach" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'underline', fontSize: '14px' }}>Back to Coach Info</Link>
        </p>
      </div>

      <AuthFooter />
    </div>
  );
}

export default CoachSignup;
